import type {
  CasOrCategory,
  FacilityID,
  ProductChemicalCompositionID,
  ProductID,
  ReleaseProfileAttestationState,
  ReleaseProfileID,
  ReleaseProfileQuestionID,
  UsageTypeID,
  UsageUnits,
} from '../types/db.ts';
import type { ReleaseProfileBrief, ReleaseProfileProcessChemical, ThresholdBrief, Warning } from '../types/tri.ts';
import { VariableNameFinder } from './formulas/variable-name-finder.ts';
import { type Expression, Num, SimpleEvalError } from './formulas/ast.ts';
import {
  notApplicableVar,
  productChemicalConcentrationVar,
  productElementalConcentrationVar,
  productSolidsPercentOfWeightVar,
  productSolidsPercentOfVolumeVar,
  productUsageLbVar,
  productUsageGalVar,
  rainfallVar,
  variablesImplyChemicalConcentration,
  variablesImplyProduct,
  wasteStreamAllocationPercentageVar,
} from './formulas/formula-utils.ts';
import {
  type ExpressionValidResultTypes,
  type ExpressionValidResultTypesSimple,
  SimpleExpressionEvaluator,
} from './formulas/evaluator.ts';
import {
  formulaElaboratedToValidOutputs,
  type FormulaToOutputsModeStrict,
  releaseProfileAnnotatableVariableNames,
  releaseProfileComputeMixResultsFromQuestionnaire,
  variablesAndChemicalsInFormulas,
} from './release-profiles.ts';
import type {
  FormulaOutputType,
  ReleaseProfileChemicalProfile,
  ReleaseProfileConstant,
  ReleaseProfileFormula,
  ReleaseProfileMixCalculation,
  ReleaseProfileMixCalculationDefinition,
  ReleaseProfileQuestion,
  ReleaseProfilesAndVariables,
  FormulaID,
  OutputID,
  PotentialEmissionsBasisPeriod,
  ReleaseProfileUsageMix,
  ReleaseProfileWasteStream,
  SubFormulaBrief,
  VarNameToUsageMixResults,
  WasteStreamID,
} from '../types/release-profiles.ts';
import { ExpressionSubstitutor, type ExpressionSubstitutorResult } from './formulas/substitutor.ts';
import { Gallon, Gram, Inch, Percent, Pound, UnitsTracker } from './formulas/units.ts';
import type {
  QuestionnaireAnswers,
  QuestionnaireFormulaChoicesContainer,
  TransferID,
  WasteTransfer,
  WasteTransferBase,
} from '../types/questionnaires.ts';
import { compoundToElementalMixPercentageConvert } from './chemical-molecular-weight-caclulations.ts';
import {
  type FormRAssignmentLine,
  type FormRLeafLine,
  formRLeafLines,
  formRLeafLineToAssignmentLine,
} from './form-r/form-r-sections.ts';
import type { TRIQuantityBasisEstimationCodeType } from '../types/form-r.ts';
import { wasteStreamToQuestionID, zeroReleasesQuestionID } from './questionnaires.ts';
import { Tokenizer } from './formulas/tokenizer.ts';
import { Parser } from './formulas/parser.ts';

function expressionEvaluateReturningNumber(
  expression: Expression,
  variables: Map<string, ExpressionValidResultTypesSimple>,
  unitsExpected: string | null,
):
  | { status: 'success'; value: number; units: string }
  | { status: 'error'; error: string; cause: SimpleEvalError | null } {
  let result: number | ExpressionValidResultTypes = new SimpleExpressionEvaluator(expression, variables).evaluate();
  let units = 'unknown';
  if (result instanceof SimpleEvalError) {
    return {
      status: 'error',
      error: `Unexpected error occurred while calculating formula: ${result.message}`,
      cause: result,
    };
  } else if (result instanceof Num) {
    if (unitsExpected && !result.units.equals(UnitsTracker.fromParsedUnitString(unitsExpected))) {
      return {
        status: 'error',
        error: `Expected formula to produce ${unitsExpected}, but it produced ${result.units.toString()} instead.`,
        cause: null,
      };
    }
  } else {
    return {
      status: 'error',
      error: `Expected formula to produce a number, but it produced ${result.toString()} instead.`,
      cause: null,
    };
  }

  units = result.units.toString();
  result = result.value;

  return { status: 'success', value: result, units };
}

export interface CalculationLine<Cas = CasOrCategory | null> {
  lineID: number; // Not stable between re-runs of the calculation, this just counts up for each line in the result.
  transferID: TransferID;
  wasteStreamID: WasteStreamID;
  releaseProfileProcessName: string;
  releaseProfileID: ReleaseProfileID | null;
  releaseProfileFormulaID: FormulaID | null;
  releaseProfileOutputID: OutputID | null;
  releaseProfileName: string;
  releaseProfileFormulaName: string;
  formRLeafLine: FormRLeafLine | '';
  basis: TRIQuantityBasisEstimationCodeType | '';
  thresholdOrRelease: 'threshold' | 'release' | 'both';
  thresholdUsageTypeID: UsageTypeID | null;
  formulaOutputType: FormulaOutputType;
  potentialBasisPeriod: PotentialEmissionsBasisPeriod;
  product: { productID: ProductID; productName: string; poundsUsed: number } | null;
  casOrCategoryOriginal: Cas;
  casOrCategoryMapped: Cas;
  computationResult: number;
  computationUnits: string;
  // When involved in TRI releases, these two fields reflect the resultant pounds or grams instead of the raw units
  // of the calculation. That said, these are primarily for display purposes and are best-effort. They will be identical
  // to computationResult and computationUnits if this line was not involved in TRI release calculations:
  convertedResult: number;
  convertedUnits: string;
  formulaWithVariables: string;
  formulasWithValues: { formula: ExpressionSubstitutorResult; value: number; units: string }[];
  subFormulas: {
    nameFriendly: string;
    nameForVariable: string;
    formula: ExpressionSubstitutorResult;
    value: number;
    units: string;
  }[];
  variablesUsed: Partial<Record<string, { value: number; units: string }>>;
}

export interface TransferComputed {
  transferID: TransferID;
  amountReleased: number;
  amountUnits: 'pounds' | 'grams';
  isFromStormwater: boolean;
  calculationLines: CalculationLine<CasOrCategory>[];
}

export interface ReleaseComputed {
  formRLine: FormRLeafLine;
  chemicalReleases: {
    casOrCategory: CasOrCategory;
    amountReleased: number;
    amountUnits: 'pounds' | 'grams';
    transfers: TransferComputed[];
  }[];
}

export interface ThresholdComputed {
  casOrCategory: CasOrCategory;
  usageTypeID: UsageTypeID;
  totalPoundsUsed: number;
  totalGramsUsed: number;
  calculationLines: CalculationLine<CasOrCategory>[];
}

interface FormulaSelection {
  formulaID: FormulaID;
  outputID: OutputID;
  potentialBasisPeriod: PotentialEmissionsBasisPeriod;
  basis: '' | TRIQuantityBasisEstimationCodeType;
  formula: string;
  subFormulas: SubFormulaBrief[];
  formulaNameFriendly: string;
  wasteStreamsInFormula: ReleaseProfileWasteStream[];
  wasteStreamSumsInFormula: ReleaseProfileWasteStream[];
  variableNames: Set<string>;
  expression: Expression;
  // none = no restrictions, include all chemicals.
  // noChemicals = no chemicals, do not evaluate any chemicals.
  chemicalRestrictions: 'none' | 'noChemicals' | 'allVolatile' | 'allSolid' | Set<CasOrCategory>;
  formulaOutputType: FormulaOutputType;
}

type FormulaSelectionResult =
  | { status: 'warnAndStop'; warning: Warning }
  | { status: 'notApplicable'; warning: Warning | null }
  | {
      status: 'success';
      warning: Warning | null;
      formulaAnnotated: FormulaSelection;
    };

function profileSelectAndExpandFormula(
  mode: FormulaToOutputsModeStrict,
  profile: Pick<ReleaseProfileBrief, 'releaseProfileID' | 'name' | 'basis' | 'formRLine'> & {
    formula: ReleaseProfileFormula;
  },
  wasteStreams: ReleaseProfileWasteStream[],
  chemicalsExceedingThresholds: Set<CasOrCategory>,
  releaseProfileFormulaChoices: QuestionnaireFormulaChoicesContainer,
  annotatableVariableNames: Set<string>,
): FormulaSelectionResult[] {
  const formulas = Array.from(
    formulaElaboratedToValidOutputs(
      mode.type === 'emissions'
        ? { type: 'emissions', acceptableOutputType: new Set([mode.acceptableOutputType]) }
        : mode,
      profile.formula,
      chemicalsExceedingThresholds,
      releaseProfileFormulaChoices,
      annotatableVariableNames,
    ),
  );

  if (formulas.length === 0) {
    return [
      {
        status: 'notApplicable',
        warning: null,
      },
    ];
  }

  return formulas.map((formulaObj): FormulaSelectionResult => {
    if (formulaObj.formula.trim() === notApplicableVar) {
      return {
        status: 'notApplicable',
        warning: null,
      };
    }

    let warning: Warning | null = null;

    if (formulaObj.answerIsMissingForChoice) {
      const { question, answerAssumed } = formulaObj.answerIsMissingForChoice;
      warning = {
        warningID: 'TW1069',
        type: 'questionnaire',
        subType: formulaObj.answerIsMissingForChoice.choiceListID,
        casOrCategory: null,
        message: `The profile "${profile.name}" contains multiple formulas, but the question ("${question}") used for picking one was not answered. Releases calculated assuming the answer "${answerAssumed}" was chosen.`,
        blocksPriorYearSection8Calculation: true,
      };
    }

    const formula = formulaObj.formula;
    const basis = formulaObj.basis || profile.basis;
    const chemicalRestrictions: FormulaSelection['chemicalRestrictions'] =
      formulaObj.chemicals === 'inferred'
        ? 'none'
        : formulaObj.chemicals === 'noChemicals' ||
            formulaObj.chemicals === 'allVolatile' ||
            formulaObj.chemicals === 'allSolid'
          ? formulaObj.chemicals
          : new Set(formulaObj.chemicals);
    const formulaNameFriendly = formulaObj.nameFriendly;

    const maybeVariableNames = VariableNameFinder.findInString(formula);
    if (maybeVariableNames.status === 'error') {
      return {
        status: 'warnAndStop',
        warning: {
          warningID: 'TW1041',
          type: 'releaseProfile',
          releaseProfileID: profile.releaseProfileID,
          releaseProfileName: profile.name,
          message: `Error with release profile ${profile.name} (${profile.releaseProfileID}): ${maybeVariableNames.error}`,
        },
      };
    }

    const wasteStreamsInFormula = wasteStreams.filter((m) => maybeVariableNames.variableNames.has(m.nameForVariable));
    const wasteStreamSumsInFormula = wasteStreams.filter((m) =>
      maybeVariableNames.variableNames.has(m.nameForVariable + '_Sum'),
    );
    const needsWasteStream = /^(5\.3|6\.1|6\.2)/.test(profile.formRLine);
    if (needsWasteStream) {
      if (wasteStreamsInFormula.length === 0) {
        warning = {
          warningID: 'TW1042',
          type: 'releaseProfile',
          releaseProfileID: profile.releaseProfileID,
          releaseProfileName: profile.name,
          message: `Expected to find waste stream in profile "${profile.name}" for Form R Line ${profile.formRLine}, but found none.`,
        };
      } else if (wasteStreamsInFormula.length >= 2) {
        warning = {
          warningID: 'TW1043',
          type: 'releaseProfile',
          releaseProfileID: profile.releaseProfileID,
          releaseProfileName: profile.name,
          message: `Expected to find a single waste stream in profile "${profile.name}" for Form R Line ${profile.formRLine}, but found ${wasteStreamsInFormula.length} streams.`,
        };
      }
    } else if (wasteStreamsInFormula.length > 0) {
      warning = {
        warningID: 'TW1044',
        type: 'releaseProfile',
        releaseProfileID: profile.releaseProfileID,
        releaseProfileName: profile.name,
        message: `Unexpected waste stream in profile "${profile.name}" for Form R Line ${profile.formRLine}.`,
      };
    }

    return {
      status: 'success',
      warning,
      formulaAnnotated: {
        formulaID: formulaObj.formulaID,
        outputID: formulaObj.outputID,
        formula,
        subFormulas: formulaObj.subFormulas,
        formulaNameFriendly,
        basis,
        wasteStreamsInFormula,
        wasteStreamSumsInFormula,
        variableNames: maybeVariableNames.variableNames,
        expression: maybeVariableNames.expression,
        chemicalRestrictions,
        formulaOutputType: formulaObj.type,
        potentialBasisPeriod: formulaObj.potentialBasisPeriod,
      },
    };
  });
}

export function zeroReleasesWasteStreamID(formRLine: FormRLeafLine, casOrCategory: CasOrCategory): WasteStreamID {
  return `waste_stream_zero_${formRLine}_${casOrCategory}`;
}

export interface WasteStreamTotalingItem {
  variableName: string;
  total: number;
  units: string;
  formulaWithValues: ExpressionSubstitutorResult;
  isFromStormwater: boolean;
  productUsageEval:
    | null
    | ((args: {
        percentage: number;
        productPounds: number;
        productGallons: number | null;
      }) => { value: number; units: string; formulasWithValues: ExpressionSubstitutorResult } | Warning);
}

export function facilityWasteStreamTotalsCalculate(
  mode: FormulaToOutputsModeStrict,
  { releaseProfiles, constants, questions, wasteStreams }: ReleaseProfilesAndVariables,
  productUsage: Map<ProductID, { totalPoundsUsed: number; totalGallonsUsed: number | null }>,
  processes: ReleasesCalculateProcessAndProfiles[],
  chemicalsExceedingThresholdsPlusOriginals: Set<CasOrCategory>,
  {
    releaseProfileAnswers,
    releaseProfileFormulaChoices,
    wasteTransfers,
  }: Pick<QuestionnaireAnswers, 'releaseProfileAnswers' | 'releaseProfileFormulaChoices' | 'wasteTransfers'>,
): {
  totals: Partial<Record<WasteStreamID, WasteStreamTotalingItem>>;
  warnings: Warning[];
} {
  const totals: Partial<Record<WasteStreamID, WasteStreamTotalingItem>> = {};
  const warnings: Warning[] = [];
  const releaseProfilesById = new Map(releaseProfiles.map((p) => [p.releaseProfileID, p]));
  const wasteStreamToProcess = new Map<
    WasteStreamID,
    {
      wasteStream: ReleaseProfileWasteStream;
      processName: string;
      releaseProfileID: ReleaseProfileID;
      releaseProfileName: string;
      productsAssociated: Map<ProductID, { usageUnits: UsageUnits }>;
    }
  >();

  for (const process of processes) {
    for (const { releaseProfileID, releaseProfileName, products } of process.profiles) {
      const profile = releaseProfilesById.get(releaseProfileID);
      if (!profile) {
        warnings.push({
          warningID: 'TW1038',
          type: 'internalConsistencyCheck',
          message: `questionsRequiredFromReleaseProfiles: Unable to find profile "${releaseProfileName}" (${releaseProfileID})`,
        });
        continue;
      }

      const selections = profileSelectAndExpandFormula(
        mode,
        profile,
        wasteStreams,
        chemicalsExceedingThresholdsPlusOriginals,
        releaseProfileFormulaChoices,
        // Waste stream formulas cannot reference other formulas, and so don't need to know which variables can be
        // annotated during formula substitution:
        new Set(),
      );

      for (const selection of selections) {
        if (selection.warning) {
          warnings.push(selection.warning);
        }

        if (selection.status === 'warnAndStop') {
          continue;
        } else if (selection.status === 'notApplicable') {
          continue;
        }

        const { wasteStreamsInFormula } = selection.formulaAnnotated;

        for (const wasteStream of wasteStreamsInFormula) {
          if (!wasteStreamToProcess.has(wasteStream.definition.wasteStreamID)) {
            wasteStreamToProcess.set(wasteStream.definition.wasteStreamID, {
              wasteStream,
              processName: process.processName,
              releaseProfileID,
              releaseProfileName,
              productsAssociated: new Map(),
            });
          }

          const productsAssociated = wasteStreamToProcess.get(wasteStream.definition.wasteStreamID)!.productsAssociated;
          for (const product of products) {
            productsAssociated.set(product.productID, product);
          }
        }
      }
    }
  }

  const sums: [string, Num][] = [];
  for (const { wasteStream } of wasteStreamToProcess.values()) {
    if (wasteStream.definition.source.type === 'questionnaire') {
      const nums: number[] = [];
      const total = Object.values(wasteTransfers).reduce((accum, curr) => {
        if (
          curr?.wasteStreamID === wasteStream.definition.wasteStreamID &&
          typeof curr.allocationOrAnswer === 'number'
        ) {
          nums.push(curr.allocationOrAnswer);
          return accum + curr.allocationOrAnswer;
        }

        return accum;
      }, 0);
      const formulaWithValues: ExpressionSubstitutorResult = [];
      for (let numIndex = 0; numIndex < nums.length; numIndex++) {
        formulaWithValues.push({ name: `Transfer #${numIndex}`, value: nums[numIndex].toFixed(2) });
        if (numIndex < nums.length - 1) {
          formulaWithValues.push('+');
        }
      }

      const variableName = wasteStream.nameForVariable + '_Sum';
      totals[wasteStream.definition.wasteStreamID] = {
        variableName,
        total,
        units: wasteStream.units,
        formulaWithValues,
        isFromStormwater: wasteStream.definition.isFromStormwater,
        productUsageEval: null,
      };
      sums.push([variableName, new Num(total, UnitsTracker.fromParsedUnitString(wasteStream.units))]);
    }
  }

  for (const {
    wasteStream,
    processName,
    releaseProfileID,
    releaseProfileName,
    productsAssociated,
  } of wasteStreamToProcess.values()) {
    if (wasteStream.definition.source.type !== 'formula') {
      continue;
    }

    const maybeVariableNamesForWasteStream = VariableNameFinder.findInString(
      `(${wasteStream.definition.source.formula}) * ${wasteStreamAllocationPercentageVar}`,
    );
    if (maybeVariableNamesForWasteStream.status === 'error') {
      warnings.push({
        warningID: 'TW1045',
        type: 'releaseProfile',
        releaseProfileID,
        releaseProfileName,
        message: maybeVariableNamesForWasteStream.error,
      });
      continue;
    }

    const { variableNames, expression } = maybeVariableNamesForWasteStream;

    let productTotalPoundsUsed = 0;
    let productTotalGallonsUsed: number | null = 0;
    for (const [productID, { usageUnits }] of productsAssociated.entries()) {
      const usage = productUsage.get(productID);
      productTotalPoundsUsed += usage?.totalPoundsUsed ?? 0;

      // Sum the gallons used for all products, but if a product is not convertible to gallons, bail out as we do
      // not want to allow use of an incorrect volume if assigned products mix mass and volume:
      if (usageUnits === 'gallons' && productTotalGallonsUsed !== null) {
        productTotalGallonsUsed += usage?.totalGallonsUsed ?? 0;
      } else {
        productTotalGallonsUsed = null;
      }
    }

    const variables = new Map<string, Num>([
      // This is unitless instead of a percentage, because it's already been reduced to 0-1:
      [wasteStreamAllocationPercentageVar, new Num(1, UnitsTracker.unitless())],
      [rainfallVar, new Num(releaseProfileAnswers['question_rainfall'] ?? 0, UnitsTracker.fromSingleUnit(Inch))],
      [productUsageLbVar, new Num(productTotalPoundsUsed, UnitsTracker.fromSingleUnit(Pound))],
      ...[productTotalGallonsUsed].flatMap((gallons): [string, Num][] => {
        if (gallons === null) {
          return [];
        } else {
          return [[productUsageGalVar, new Num(gallons, UnitsTracker.fromSingleUnit(Gallon))]];
        }
      }),
      ...constants.map((c): [string, Num] => [
        c.nameForVariable,
        new Num(c.definition.value, UnitsTracker.fromParsedUnitString(c.units)),
      ]),
      ...questions.flatMap((q): [string, Num][] => {
        const questionID = q.definition.questionID;
        if (!variableNames.has(q.nameForVariable)) {
          return [];
        }

        if (q.definition.isChemicalSpecific) {
          return [];
        }

        const answer = releaseProfileAnswers[questionID];
        if (answer === undefined) {
          warnings.push({
            warningID: 'TW1046',
            type: 'releaseProfile',
            releaseProfileID,
            releaseProfileName,
            message: `The question "${q.description}" from the release profile "${releaseProfileName}" on the process "${processName}" was not answered.`,
          });
        }

        return [[q.nameForVariable, new Num(answer ?? 0, UnitsTracker.fromParsedUnitString(q.units))]];
      }),
      ...sums,
    ]);

    const result = expressionEvaluateReturningNumber(expression, variables, wasteStream.units);
    if (result.status === 'success') {
      let productUsageEval: WasteStreamTotalingItem['productUsageEval'] = null;
      if (variableNames.has(productUsageLbVar) || variableNames.has(productUsageGalVar)) {
        productUsageEval = (args) => {
          variables.set(wasteStreamAllocationPercentageVar, new Num(args.percentage, UnitsTracker.unitless()));
          variables.set(productUsageLbVar, new Num(args.productPounds, UnitsTracker.fromSingleUnit(Pound)));
          if (args.productGallons !== null) {
            variables.set(productUsageGalVar, new Num(args.productGallons, UnitsTracker.fromSingleUnit(Gallon)));
          } else {
            variables.delete(productUsageGalVar);
          }
          const productResult = expressionEvaluateReturningNumber(expression, variables, wasteStream.units);
          if (productResult.status === 'success') {
            return {
              value: productResult.value,
              formulasWithValues: ExpressionSubstitutor.substitute(expression, variables),
              units: productResult.units,
            };
          } else {
            return {
              warningID: 'TW1055',
              type: 'releaseProfile',
              releaseProfileID,
              releaseProfileName,
              message: `While calculating product-specific waste stream "${wasteStream.nameFriendly}" (used in process "${processName}" with profile "${releaseProfileName}") an unexpected error occurred: ${productResult.error}`,
            };
          }
        };
      }

      totals[wasteStream.definition.wasteStreamID] = {
        variableName: wasteStream.nameForVariable,
        total: result.value,
        units: result.units,
        formulaWithValues: ExpressionSubstitutor.substitute(expression, variables),
        isFromStormwater: wasteStream.definition.isFromStormwater,
        productUsageEval,
      };
    } else {
      warnings.push({
        warningID: 'TW1047',
        type: 'releaseProfile',
        releaseProfileID,
        releaseProfileName,
        message: `While calculating waste stream "${wasteStream.nameFriendly}" (used in process "${processName}" with profile "${releaseProfileName}") an unexpected error occurred: ${result.error}`,
      });
    }
  }

  return { totals, warnings };
}

// ReleaseProfileProcessWithChildren minimized for just what the triReleasesCalculate needs:
export interface ReleasesCalculateProcessAndProfiles {
  formRLine: FormRAssignmentLine | '' | null;
  processName: string;
  facilityID: FacilityID;
  profiles: {
    releaseProfileID: ReleaseProfileID;
    releaseProfileName: string;
    thresholdOrRelease: 'release' | 'threshold' | 'both';
    thresholdUsageTypeID: UsageTypeID | null;
    products: {
      productID: ProductID;
      productName: string;
      usageUnits: UsageUnits;
      solidsPercentageByWeight: number | null;
      solidsPercentageByVolume: number | null;
      chemicalsExcluded: CasOrCategory[];
      chemicals: ReleaseProfileProcessChemical[];
    }[];
  }[];
}

interface FormulaChosenIsNa {
  type: 'notApplicable';
  formRLeafLine: FormRLeafLine | '';
}

interface ProductSeen {
  type: 'productSeen';
  productID: ProductID;
}

function* variablesPrepare({
  chemicalsRelevant,
  constants,
  questionsInFormula,
  chemicalSpecificQuestions,
  chemicalProfilesInFormula,
  mixCalculationsInFormula,
  usageMixesInFormula,
  wasteStreamsInFormula,
  wasteStreamSumsInFormula,
  releaseProfileAnswers,
  releaseProfileChemicalNaAnswers,
  wasteTransfers,
  usageMixResults,
  wasteStreamTotals,
  processName,
  releaseProfileName,
  formRLine,
  product,
  productChemical,
  casOrCategoryCurrent,
  transfer,
}: {
  chemicalsRelevant: Set<CasOrCategory>;

  constants: ReleaseProfileConstant[];
  questionsInFormula: ReleaseProfileQuestion[];
  chemicalSpecificQuestions: Map<ReleaseProfileQuestionID, Set<CasOrCategory>>;
  chemicalProfilesInFormula: ReleaseProfileChemicalProfile[];
  mixCalculationsInFormula: ReleaseProfileMixCalculation[];
  usageMixesInFormula: ReleaseProfileUsageMix[];
  wasteStreamsInFormula: ReleaseProfileWasteStream[];
  wasteStreamSumsInFormula: ReleaseProfileWasteStream[];

  releaseProfileAnswers: QuestionnaireAnswers['releaseProfileAnswers'];
  releaseProfileChemicalNaAnswers: QuestionnaireAnswers['releaseProfileChemicalNaAnswers'];
  wasteTransfers: QuestionnaireAnswers['wasteTransfers'];
  usageMixResults: VarNameToUsageMixResults;
  wasteStreamTotals: Partial<Record<WasteStreamID, WasteStreamTotalingItem>>;

  processName: string;
  releaseProfileName: string;
  formRLine: FormRAssignmentLine | '' | null;
  product: {
    productID: ProductID | null;
    productName: string;
    productTotalPoundsUsed: number;
    productTotalGallonsUsed: number | null;
    solidsPercentageByWeight: number | null;
    solidsPercentageByVolume: number | null;
  };
  productChemical: ReleaseProfileProcessChemical | null;
  casOrCategoryCurrent: CasOrCategory | null;
  transfer: WasteTransfer | (WasteTransferBase & { type: 'unknown' });
}): Generator<
  | { nameForVariable: string; casOrCategory: CasOrCategory | null; value: Num }
  | CalculationLine['formulasWithValues'][number]
  | ProductSeen
  | Warning,
  void,
  void
> {
  yield {
    nameForVariable: rainfallVar,
    casOrCategory: null,
    value: new Num(releaseProfileAnswers['question_rainfall'] ?? 0, UnitsTracker.fromSingleUnit(Inch)),
  };

  yield {
    nameForVariable: productUsageLbVar,
    casOrCategory: null,
    value: new Num(product.productTotalPoundsUsed, UnitsTracker.fromSingleUnit(Pound)),
  };

  if (product.productTotalGallonsUsed !== null) {
    yield {
      nameForVariable: productUsageGalVar,
      casOrCategory: null,
      value: new Num(product.productTotalGallonsUsed, UnitsTracker.fromSingleUnit(Gallon)),
    };
  }

  yield {
    nameForVariable: productSolidsPercentOfWeightVar,
    casOrCategory: null,
    value: new Num(product.solidsPercentageByWeight ?? 0, UnitsTracker.fromSingleUnit(Percent)),
  };

  yield {
    nameForVariable: productSolidsPercentOfVolumeVar,
    casOrCategory: null,
    value: new Num(product.solidsPercentageByVolume ?? 0, UnitsTracker.fromSingleUnit(Percent)),
  };

  if (productChemical) {
    const elementalConcentration = compoundToElementalMixPercentageConvert(productChemical);

    // These list casOrCategory: null because the product chemical concentration variables are not annotatable (i.e.
    //   you cannot say Chemical_Concentration_in_Product[N420])
    // These are unitless instead of a percentage, because they've already been reduced to 0-1:
    yield {
      nameForVariable: productChemicalConcentrationVar,
      casOrCategory: null,
      value: new Num(productChemical.concentrationComputed, UnitsTracker.unitless()),
    };
    yield {
      nameForVariable: productElementalConcentrationVar,
      casOrCategory: null,
      value: new Num(elementalConcentration ?? productChemical.concentrationComputed, UnitsTracker.unitless()),
    };
  }

  for (const constant of constants) {
    yield {
      nameForVariable: constant.nameForVariable,
      casOrCategory: null,
      value: new Num(constant.definition.value, UnitsTracker.fromParsedUnitString(constant.units)),
    };
  }

  for (const question of questionsInFormula) {
    let chemicalsForQuestion: Set<CasOrCategory> | [null];

    if (question.definition.isChemicalSpecific) {
      chemicalsForQuestion = chemicalSpecificQuestions.get(question.definition.questionID) ?? new Set();
      if (chemicalsForQuestion.size === 0) {
        yield {
          warningID: 'TW1106',
          type: 'internalConsistencyCheck',
          message: `The question "${question.description}" from the release profile "${releaseProfileName}" on the process "${processName}" is marked as chemical-specific, but no chemicals were found in the questionnaire for this question.`,
        };
      }
    } else {
      chemicalsForQuestion = [null];
    }

    for (const casOrCategory of chemicalsForQuestion) {
      const questionID = casOrCategory
        ? (`${question.definition.questionID}_${casOrCategory}` as const)
        : question.definition.questionID;
      const answer = releaseProfileAnswers[questionID];

      if (casOrCategory && releaseProfileChemicalNaAnswers[questionID]) {
        continue;
      }

      if (answer === undefined && (!casOrCategory || casOrCategory === casOrCategoryCurrent)) {
        yield {
          warningID: 'TW1050',
          type: 'questionnaire',
          subType: questionID,
          casOrCategory: casOrCategory ?? casOrCategoryCurrent,
          message: `The question "${question.description}" from the release profile "${releaseProfileName}" on the process "${processName}" was not answered.`,
          blocksPriorYearSection8Calculation: true,
        };
      }

      yield {
        nameForVariable: question.nameForVariable,
        casOrCategory,
        value: new Num(answer ?? 0, UnitsTracker.fromParsedUnitString(question.units)),
      };
    }
  }

  // TODO: 1. Need a better (and dismissable) warning if we drop a profile due to lack of relevant chemical (i.e. The "Welding" release profile does not include a value for "{{CAS}}: {{name}}", so no release of "{{CAS}}" will be quantified. If this is intentional, you may dismiss this warning. Otherwise, please add "{{CAS}}" to the profile.):
  for (const chemicalProfile of chemicalProfilesInFormula) {
    for (const chemical of chemicalProfile.definition.chemicals) {
      yield {
        nameForVariable: chemicalProfile.nameForVariable,
        casOrCategory: chemical.casOrCategory,
        value: new Num(chemical.value, UnitsTracker.fromParsedUnitString(chemicalProfile.units)),
      };
    }
  }

  for (const mix of mixCalculationsInFormula) {
    let results: ReleaseProfileMixCalculationDefinition['results'];
    if (mix.definition.percentageType === 'static') {
      results = mix.definition.results;
    } else {
      const computed = releaseProfileComputeMixResultsFromQuestionnaire(mix, releaseProfileAnswers);
      results = computed.results;
      yield* computed.warnings.map((warning) => ({
        ...warning,
        message: `${warning.message} for release profile "${releaseProfileName}" on the process "${processName}"`,
      }));
    }

    for (const [casOrCategory, percentage] of Object.entries(results)) {
      yield {
        nameForVariable: mix.nameForVariable,
        casOrCategory: casOrCategory as CasOrCategory,
        value: new Num(percentage!, UnitsTracker.fromSingleUnit(Percent)),
      };
    }
  }

  for (const mix of usageMixesInFormula) {
    for (const productID of usageMixResults[mix.nameForVariable]?.productsUsed ?? []) {
      yield { type: 'productSeen', productID };
    }

    for (const casOrCategory of chemicalsRelevant) {
      const result = usageMixResults[mix.nameForVariable]?.percentages[casOrCategory];
      if (result) {
        // This is unitless instead of a percentage, because it's already been reduced to 0-1:
        yield { nameForVariable: mix.nameForVariable, casOrCategory, value: new Num(result, UnitsTracker.unitless()) };
      }
    }
  }

  for (const wasteStream of wasteStreamsInFormula) {
    if (wasteStream.definition.wasteStreamID !== transfer.wasteStreamID) {
      continue;
    }

    if (wasteStream.definition.source.type === 'questionnaire') {
      if (
        transfer.allocationOrAnswer === '' ||
        transfer.allocationOrAnswer === 'all' ||
        transfer.allocationOrAnswer === 'remaining'
      ) {
        yield {
          warningID: 'TW1051',
          type: 'questionnaire',
          subType: transfer.transferID,
          casOrCategory: casOrCategoryCurrent,
          message: `The question "${wasteStream.description}" from the release profile "${releaseProfileName}" on the process "${processName}" for the waste stream "${wasteStream.nameForVariable}" was not answered.`,
          blocksPriorYearSection8Calculation: true,
        };
        continue;
      } else {
        yield {
          nameForVariable: wasteStream.nameForVariable,
          casOrCategory: null,
          value: new Num(transfer.allocationOrAnswer, UnitsTracker.fromParsedUnitString(wasteStream.units)),
        };
      }
    } else {
      if (transfer.allocationOrAnswer === '') {
        yield {
          warningID: 'TW1052',
          type: 'questionnaire',
          subType: transfer.transferID,
          casOrCategory: casOrCategoryCurrent,
          message: `The question "${wasteStream.description}" from the release profile "${releaseProfileName}" on the process "${processName}" for the waste stream "${wasteStream.nameForVariable}" was not answered.`,
          blocksPriorYearSection8Calculation: true,
        };
        continue;
      }

      const totalsDoc = wasteStreamTotals[transfer.wasteStreamID];
      if (!totalsDoc) {
        yield {
          warningID: 'TW1053',
          type: 'internalConsistencyCheck',
          message: `The question "${wasteStream.description}" from the release profile "${releaseProfileName}" on the process "${processName}" for the waste stream "${wasteStream.nameForVariable}" required the waste stream total to be computed, but we were unable to compute it.`,
        };
        continue;
      }

      let answer: number;
      if (transfer.allocationOrAnswer === 'all' || transfer.allocationOrAnswer === 'remaining') {
        if (transfer.allocationOrAnswer === 'all') {
          answer = totalsDoc.total;
        } else {
          answer =
            totalsDoc.total -
            Object.values(wasteTransfers).reduce((accum, transfer) => {
              if (
                transfer?.wasteStreamID === wasteStream.definition.wasteStreamID &&
                typeof transfer?.allocationOrAnswer === 'number'
              ) {
                return accum + transfer.allocationOrAnswer;
              } else {
                return accum;
              }
            }, 0);
        }
      } else {
        answer = transfer.allocationOrAnswer;
      }

      // At this point in our nested loop, we are evaluating formulas for each chemical-transfer-product
      // triplet. If the waste stream includes product usage, we can't use the precomputed waste stream
      // total directly here, as that represents the _whole_ waste stream, not the contribution of this
      // individual transfer of product that we are considering at this point in the code.
      //
      // Therefore, if the waste stream total was computed from product usage, we look at the percentage
      // of the total waste stream that the current transfer represents, and we re-evaluate the waste
      // stream formula using only that percentage and the current product's usage pounds.
      //
      // This also ensures that if the release profile formula includes product chemical concentration,
      // that we aren't applying an individual product's concentration to a whole waste stream that derives
      // from a mix of products, which is invalid.
      if (totalsDoc.productUsageEval) {
        if (!product.productID) {
          yield {
            warningID: 'TW1054',
            type: 'releaseProfileAssignment',
            formRLine: formRLine || '5.1',
            message: `The question "${wasteStream.description}" from the release profile "${releaseProfileName}" on the process "${processName}" for the waste stream "${wasteStream.nameForVariable}" required the waste stream to be evaluated against a product, but we could not find one.`,
          };
          continue;
        }

        const productAnswer = totalsDoc.productUsageEval({
          percentage: totalsDoc.total === 0 ? 0 : answer / totalsDoc.total,
          productPounds: product.productTotalPoundsUsed,
          productGallons: product.productTotalGallonsUsed,
        });
        if ('warningID' in productAnswer) {
          yield productAnswer;
          continue;
        }

        answer = productAnswer.value;
        productAnswer.formulasWithValues.unshift(wasteStream.nameForVariable, '=');
        yield {
          formula: productAnswer.formulasWithValues,
          value: productAnswer.value,
          units: productAnswer.units,
        };
      }

      yield {
        nameForVariable: wasteStream.nameForVariable,
        casOrCategory: null,
        value: new Num(answer, UnitsTracker.fromParsedUnitString(wasteStream.units)),
      };
    }
  }

  for (const wasteStream of wasteStreamSumsInFormula) {
    yield {
      nameForVariable: wasteStream.nameForVariable + '_Sum',
      casOrCategory: null,
      value: new Num(
        wasteStreamTotals[wasteStream.definition.wasteStreamID]?.total ?? 0,
        UnitsTracker.fromParsedUnitString(wasteStream.units),
      ),
    };
  }
}

export function* chemicalCalculationsExecute(
  mode: FormulaToOutputsModeStrict,
  profilesAndVariables: ReleaseProfilesAndVariables,
  productUsage: Map<ProductID, { totalPoundsUsed: number; totalGallonsUsed: number | null }>,
  chemicalsRelevant: Set<CasOrCategory>,
  chemicalsToTriCategories: Map<CasOrCategory, CasOrCategory[]>,
  processes: ReleasesCalculateProcessAndProfiles[],
  {
    releaseProfileAnswers,
    releaseProfileChemicalNaAnswers,
    releaseProfileFormulaChoices,
    wasteTransfers,
  }: Pick<
    QuestionnaireAnswers,
    'releaseProfileAnswers' | 'releaseProfileChemicalNaAnswers' | 'releaseProfileFormulaChoices' | 'wasteTransfers'
  >,
  usageMixResults: VarNameToUsageMixResults,
  wasteStreamTotals: Partial<Record<WasteStreamID, WasteStreamTotalingItem>>,
  chemicalSpecificQuestions: Map<ReleaseProfileQuestionID, Set<CasOrCategory>>,
): Generator<Warning | ProductSeen | FormulaChosenIsNa | CalculationLine, void, void> {
  const { releaseProfiles, constants, wasteStreams } = profilesAndVariables;
  const annotatableNames = releaseProfileAnnotatableVariableNames(profilesAndVariables);
  const releaseProfilesById = new Map(releaseProfiles.map((p) => [p.releaseProfileID, p]));
  let lineCount = 0;

  for (const process of processes) {
    for (const {
      releaseProfileID,
      releaseProfileName,
      products,
      thresholdOrRelease,
      thresholdUsageTypeID,
    } of process.profiles) {
      const profile = releaseProfilesById.get(releaseProfileID);
      if (!profile) {
        yield {
          warningID: 'TW1048',
          type: 'internalConsistencyCheck',
          message: `questionsRequiredFromReleaseProfiles: Unable to find profile "${releaseProfileName}" (${releaseProfileID})`,
        };
        continue;
      }

      const selections = profileSelectAndExpandFormula(
        mode,
        profile,
        wasteStreams,
        chemicalsRelevant,
        releaseProfileFormulaChoices,
        annotatableNames,
      );
      for (const selection of selections) {
        if (selection.status === 'warnAndStop') {
          yield selection.warning;
          continue;
        } else if (selection.status === 'notApplicable') {
          yield { type: 'notApplicable', formRLeafLine: profile.formRLine };
          continue;
        } else if (selection.warning) {
          yield selection.warning;
        }

        const {
          formula,
          subFormulas: subFormulaStrings,
          wasteStreamsInFormula,
          wasteStreamSumsInFormula,
          variableNames,
          expression,
          formulaOutputType,
          potentialBasisPeriod,
        } = selection.formulaAnnotated;

        let wasteStreamHasProductUsage = false;
        for (const wasteStream of wasteStreamsInFormula) {
          if (wasteStreamTotals[wasteStream.definition.wasteStreamID]?.productUsageEval) {
            wasteStreamHasProductUsage = true;
            break;
          }
        }

        const {
          chemicalsFromVariables,
          questionsInFormula,
          mustSkipFormulaDueToNa,
          chemicalProfilesInFormula,
          mixCalculationsInFormula,
          usageMixesInFormula,
        } = variablesAndChemicalsInFormulas(
          chemicalsRelevant,
          profilesAndVariables,
          usageMixResults,
          releaseProfileChemicalNaAnswers,
          expression,
        );
        const productChemicalConcentrations = new Map<
          `${ProductID}-${CasOrCategory}`,
          ReleaseProfileProcessChemical[]
        >();
        const chemicalsInProducts = new Set<CasOrCategory>();
        for (const product of products) {
          for (const chemical of product.chemicals) {
            if (product.chemicalsExcluded.includes(chemical.casOrCategoryMapped)) {
              continue;
            }

            const key = `${product.productID}-${chemical.casOrCategoryMapped}` as const;
            if (!productChemicalConcentrations.has(key)) {
              productChemicalConcentrations.set(key, []);
            }

            productChemicalConcentrations.get(key)!.push(chemical);
            chemicalsInProducts.add(chemical.casOrCategoryMapped);
          }
        }

        let chemicalsFromProductAndProfile: Set<CasOrCategory | null>;
        if (selection.formulaAnnotated.chemicalRestrictions === 'none') {
          chemicalsFromProductAndProfile = new Set<CasOrCategory>([...chemicalsInProducts, ...chemicalsFromVariables]);
        } else if (selection.formulaAnnotated.chemicalRestrictions === 'noChemicals') {
          chemicalsFromProductAndProfile = new Set([null]);
        } else if (
          selection.formulaAnnotated.chemicalRestrictions === 'allVolatile' ||
          selection.formulaAnnotated.chemicalRestrictions === 'allSolid'
        ) {
          const targetType = selection.formulaAnnotated.chemicalRestrictions === 'allVolatile' ? 'volatile' : 'solids';
          const matchingChemicals = new Set<CasOrCategory>();
          for (const product of products) {
            for (const chemical of product.chemicals) {
              if (product.chemicalsExcluded.includes(chemical.casOrCategoryMapped)) {
                continue;
              }
              if (chemical.volatileOrSolids === targetType) {
                matchingChemicals.add(chemical.casOrCategoryMapped);
              } else if (chemical.volatileOrSolids === '' && chemicalsRelevant.has(chemical.casOrCategoryMapped)) {
                yield {
                  warningID: 'TW1115',
                  type: 'product',
                  productID: product.productID,
                  productName: product.productName,
                  message: `The product ${product.productName} contains the chemical ${chemical.chemicalName} (${chemical.casOrCategoryMapped}) with no volatile or solids classification. This classification is needed for release calculations.`,
                };
              }
            }
          }
          chemicalsFromProductAndProfile = matchingChemicals;
        } else {
          chemicalsFromProductAndProfile = selection.formulaAnnotated.chemicalRestrictions;
        }

        for (const casOrCategory of chemicalsFromProductAndProfile) {
          if (casOrCategory !== null && !chemicalsRelevant.has(casOrCategory)) {
            continue;
          }

          // TODO: 3. Release Calculations - In a future change we should consider emitting some kind of note so that
          //   it is clear on the Release Review page why something is coming up as N/A:
          if (casOrCategory !== null && mustSkipFormulaDueToNa(casOrCategory)) {
            continue;
          }

          const transfersRelevant: (WasteTransfer | (WasteTransferBase & { type: 'unknown' }))[] = Object.values(
            wasteTransfers,
          ).filter((t) =>
            wasteStreamsInFormula.find((s) => s.definition.wasteStreamID === t!.wasteStreamID),
          ) as WasteTransfer[];
          if (transfersRelevant.length === 0) {
            if (wasteStreamsInFormula.length === 1 && wasteStreamsInFormula[0].definition.source.type === 'formula') {
              transfersRelevant.push({
                type: 'unknown',
                transferID: 'transfer_default',
                wasteStreamID: wasteStreamsInFormula[0].definition.wasteStreamID,
                allocationOrAnswer: 'all',
              });
            } else if (
              wasteStreamsInFormula.length === 1 &&
              wasteStreamsInFormula[0].definition.source.type === 'questionnaire'
            ) {
              yield {
                warningID: 'TW1071',
                type: 'questionnaire',
                subType: wasteStreamToQuestionID(wasteStreamsInFormula[0]),
                casOrCategory,
                message: `The question "${wasteStreamsInFormula[0].description}" from the release profile "${releaseProfileName}" on the process "${process.processName}" for the waste stream "${wasteStreamsInFormula[0].nameForVariable}" was not answered.`,
                blocksPriorYearSection8Calculation: true,
              };
              continue;
            } else {
              transfersRelevant.push({
                type: 'unknown',
                transferID: 'transfer_default',
                wasteStreamID: 'waste_stream_none',
                allocationOrAnswer: 'all',
              });
            }
          }

          for (const transfer of transfersRelevant) {
            if (transfer.type === 'nothing') {
              continue;
            }

            const transferID = transfer.transferID;

            const productUsageAndChemicalComposition: {
              productID: ProductID | null;
              productName: string;
              productTotalPoundsUsed: number;
              productTotalGallonsUsed: number | null;
              solidsPercentageByWeight: number | null;
              solidsPercentageByVolume: number | null;
              chemicals: (ReleaseProfileProcessChemical | null)[];
            }[] = [];

            // If we rely on any product data, we will need to evaluate the formula once for every relevant chemical
            // within every Product assigned in the Process:
            if (variablesImplyProduct(variableNames) || wasteStreamHasProductUsage) {
              for (const product of products) {
                let chemicals: (ReleaseProfileProcessChemical | null)[];
                if (casOrCategory) {
                  chemicals = productChemicalConcentrations.get(`${product.productID}-${casOrCategory}`) ?? [
                    {
                      productChemicalCompositionID: 0 as ProductChemicalCompositionID,
                      casOrCategoryMapped: casOrCategory,
                      casOrCategoryOriginal: casOrCategory,
                      chemicalName: casOrCategory,
                      concentrationMin: null,
                      concentrationMax: null,
                      concentrationSolo: 0,
                      concentrationComputed: 0,
                      elementalConversionEligible: true,
                      volatileOrSolids: '' as const,
                    },
                  ];
                  // If we don't use the concentration in the formula, we don't want to loop multiple times below and
                  // double-count non-product-concentration values:
                  if (!variablesImplyChemicalConcentration(variableNames)) {
                    chemicals = chemicals.slice(0, 1);
                  }
                } else {
                  chemicals = [null];
                }

                const usage = productUsage.get(product.productID);
                let productTotalGallonsUsed: number | null;
                if (product.usageUnits === 'gallons') {
                  productTotalGallonsUsed = usage?.totalGallonsUsed ?? 0;
                } else {
                  productTotalGallonsUsed = null;
                }

                productUsageAndChemicalComposition.push({
                  productID: product.productID,
                  productName: product.productName,
                  productTotalPoundsUsed: usage?.totalPoundsUsed ?? 0,
                  productTotalGallonsUsed,
                  solidsPercentageByWeight: product.solidsPercentageByWeight,
                  solidsPercentageByVolume: product.solidsPercentageByVolume,
                  chemicals,
                });
              }
            } else {
              // If no product-specific variables are used, all release calculations must involve constants or questions
              // which have been determined without usage being involved (e.g. testing water for contamination):
              productUsageAndChemicalComposition.push({
                productID: null,
                productName: 'N/A',
                productTotalPoundsUsed: 0,
                productTotalGallonsUsed: null,
                solidsPercentageByWeight: null,
                solidsPercentageByVolume: null,
                chemicals: casOrCategory
                  ? [
                      {
                        productChemicalCompositionID: 0 as ProductChemicalCompositionID,
                        casOrCategoryMapped: casOrCategory,
                        casOrCategoryOriginal: casOrCategory,
                        chemicalName: casOrCategory,
                        concentrationMin: null,
                        concentrationMax: null,
                        concentrationSolo: 0,
                        concentrationComputed: 0,
                        elementalConversionEligible: true,
                        volatileOrSolids: '' as const,
                      },
                    ]
                  : [null],
              });
            }

            for (const product of productUsageAndChemicalComposition) {
              if (product.productID) {
                yield { type: 'productSeen', productID: product.productID };
              }

              // It's important to note that we are not looping over all product chemicals here, as before we get to
              // this point, we have made it so product.chemicals only includes items matching `casOrCategory`:
              for (const chemical of product.chemicals) {
                const formulasWithValues: CalculationLine['formulasWithValues'] = [];
                const variables = new Map<string, Num>();
                for (const variable of variablesPrepare({
                  chemicalsRelevant,

                  constants,
                  questionsInFormula,
                  chemicalSpecificQuestions,
                  chemicalProfilesInFormula,
                  mixCalculationsInFormula,
                  usageMixesInFormula,
                  wasteStreamsInFormula,
                  wasteStreamSumsInFormula,

                  releaseProfileAnswers,
                  releaseProfileChemicalNaAnswers,
                  wasteTransfers,
                  usageMixResults,
                  wasteStreamTotals,

                  processName: process.processName,
                  releaseProfileName: profile.name,
                  formRLine: process.formRLine,
                  product,
                  productChemical: chemical,
                  casOrCategoryCurrent: casOrCategory,
                  transfer,
                })) {
                  if ('warningID' in variable || 'productID' in variable) {
                    yield variable;
                  } else if ('formula' in variable) {
                    formulasWithValues.push(variable);
                  } else {
                    if (variable.casOrCategory === null || variable.casOrCategory === casOrCategory) {
                      variables.set(variable.nameForVariable, variable.value);
                    }

                    if (variable.casOrCategory) {
                      variables.set(variable.nameForVariable + '[' + variable.casOrCategory + ']', variable.value);
                    }
                  }
                }

                const result = expressionEvaluateReturningNumber(expression, variables, null);

                if (result.status === 'success') {
                  formulasWithValues.push({
                    formula: ExpressionSubstitutor.substitute(expression, variables),
                    value: result.value,
                    units: result.units,
                  });

                  const subFormulas: CalculationLine['subFormulas'] = [];
                  const subFormulaVariables = new Map(variables);
                  for (const subFormula of subFormulaStrings) {
                    const tokens = Tokenizer.tokenize(subFormula.formula);
                    if ('error' in tokens) {
                      // Given that we just successfully executed the base expression, failure here is exceedingly unlikely.
                      console.error('Unable to tokenize subformula', subFormula.formula, tokens);
                      continue;
                    }

                    const parsed = Parser.parseExpression(tokens.tokens);
                    if ('error' in parsed) {
                      // Given that we just successfully executed the base expression, failure here is exceedingly unlikely.
                      console.error('Unable to parse subformula', subFormula.formula, parsed);
                      continue;
                    }

                    const subFormulaResult = expressionEvaluateReturningNumber(
                      parsed.expression,
                      subFormulaVariables,
                      null,
                    );
                    if (subFormulaResult.status === 'error') {
                      // Given that we just successfully executed the base expression, failure here is exceedingly unlikely.
                      console.error('Unable to evaluate subformula', subFormula.formula, subFormulaResult);
                      continue;
                    }

                    subFormulaVariables.set(
                      subFormula.nameForVariable,
                      new Num(subFormulaResult.value, UnitsTracker.fromParsedUnitString(subFormulaResult.units)),
                    );

                    subFormulas.push({
                      nameFriendly: subFormula.nameFriendly,
                      nameForVariable: subFormula.nameForVariable,
                      formula: ExpressionSubstitutor.substitute(parsed.expression, subFormulaVariables),
                      value: subFormulaResult.value,
                      units: subFormulaResult.units,
                    });
                  }

                  const basis = selection.formulaAnnotated.basis;
                  const variablesUsed: CalculationLine['variablesUsed'] = {};

                  for (const variableName of variableNames) {
                    const value = variables.get(variableName);
                    if (value) {
                      variablesUsed[variableName] = { value: value.value, units: value.units.toString() };
                    }
                  }

                  const casOrCategoriesMapped = casOrCategory
                    ? (chemicalsToTriCategories.get(casOrCategory) ?? [casOrCategory])
                    : [null];

                  for (const casOrCategoryMapped of casOrCategoriesMapped) {
                    const line: CalculationLine = {
                      lineID: ++lineCount,
                      transferID,
                      wasteStreamID: transfer.wasteStreamID,
                      releaseProfileProcessName: process.processName,
                      releaseProfileID,
                      releaseProfileFormulaID: selection.formulaAnnotated.formulaID,
                      releaseProfileOutputID: selection.formulaAnnotated.outputID,
                      releaseProfileName,
                      releaseProfileFormulaName: selection.formulaAnnotated.formulaNameFriendly,
                      formRLeafLine: profile.formRLine,
                      basis,
                      thresholdOrRelease,
                      thresholdUsageTypeID,
                      formulaOutputType,
                      potentialBasisPeriod,
                      product: product.productID
                        ? {
                            productID: product.productID,
                            productName: product.productName,
                            poundsUsed: product.productTotalPoundsUsed,
                          }
                        : null,
                      casOrCategoryOriginal: chemical ? chemical.casOrCategoryOriginal : null,
                      casOrCategoryMapped,
                      computationResult: result.value,
                      computationUnits: result.units,
                      convertedResult: result.value,
                      convertedUnits: result.units,
                      formulaWithVariables: formula,
                      subFormulas,
                      formulasWithValues,
                      variablesUsed,
                    };

                    if (!Number.isFinite(line.computationResult)) {
                      yield {
                        warningID: 'TW1116',
                        type: 'releaseProfile',
                        releaseProfileID,
                        releaseProfileName,
                        message: `While calculating process "${process.processName}" with profile "${releaseProfileName}"${product.productID ? ` for product with ID "${product.productID}"` : ''} for chemical "${casOrCategory}" with the formula "${formula}" we received a ${line.computationResult} result, which usually means the formula divided by zero. This is rarely intended and usually indicates a mistake in Questionnaire or Chemical Profile data entry.`,
                      };
                      line.computationResult = 0;
                    }

                    yield line;
                  }
                } else {
                  if (result.cause?.details?.type === 'missingVariable') {
                    yield {
                      warningID: 'TW1109',
                      type: 'releaseProfile',
                      releaseProfileID,
                      releaseProfileName,
                      message: `While calculating process "${process.processName}" with profile "${releaseProfileName}"${product.productID ? ` for product with ID "${product.productID}"` : ''} for chemical "${casOrCategory}" with the formula "${formula}" we were unable to find a value for the following variable: ${result.cause.details.nameWithAnnotation}. Usually this means a chemical profile is missing a value for this chemical, but it may also mean that a formula needs to list the exact chemicals it applies to, or a variable or formula reference may need an annotation added to restrict its scope.`,
                    };
                  } else {
                    yield {
                      warningID: 'TW1057',
                      type: 'releaseProfile',
                      releaseProfileID,
                      releaseProfileName,
                      message: `While calculating process "${process.processName}" with profile "${releaseProfileName}"${product.productID ? ` for product with ID "${product.productID}"` : ''} for chemical "${casOrCategory}" with the formula "${formula}" an unexpected error occurred: ${result.error}`,
                    };
                  }
                }
              }
            }
          }
        }
      }
    }
  }
}

export function triReleasesCalculate(
  mode: FormulaToOutputsModeStrict,
  profilesAndVariables: ReleaseProfilesAndVariables,
  productUsageList: { productID: ProductID; totalPoundsUsed: number; totalGallonsUsed: number | null }[],
  thresholdsExceeded: Pick<ThresholdBrief, 'casOrCategory' | 'casOriginals'>[],
  chemicalsToTriCategories: Map<CasOrCategory, CasOrCategory[]>,
  processes: ReleasesCalculateProcessAndProfiles[],
  attestations: Record<FormRLeafLine, ReleaseProfileAttestationState>,
  {
    releaseProfileAnswers,
    releaseProfileChemicalNaAnswers,
    releaseProfileFormulaChoices,
    wasteTransfers,
  }: Pick<
    QuestionnaireAnswers,
    'releaseProfileAnswers' | 'releaseProfileChemicalNaAnswers' | 'releaseProfileFormulaChoices' | 'wasteTransfers'
  >,
  usageMixResults: VarNameToUsageMixResults,
  chemicalSpecificQuestions: Map<ReleaseProfileQuestionID, Set<CasOrCategory>>,
): {
  warnings: Warning[];
  chemicalsWithoutReleases: { casOrCategory: CasOrCategory; formRLine: FormRLeafLine }[];
  thresholdsFromFormulas: ThresholdComputed[]; // Product usage is not included in these numbers.
  releases: ReleaseComputed[];
  wasteStreamTotals: Partial<Record<WasteStreamID, WasteStreamTotalingItem>>;
  productUseStatus: Map<ProductID, 'unused' | 'used'>;
} {
  const chemicalsExceedingThresholds = new Set<CasOrCategory>(thresholdsExceeded.map((t) => t.casOrCategory));
  const chemicalsPlusOriginals = new Set<CasOrCategory>(chemicalsExceedingThresholds);
  for (const { casOriginals } of thresholdsExceeded) {
    for (const cas of casOriginals) {
      chemicalsPlusOriginals.add(cas);
    }
  }

  const productUsage = new Map(productUsageList.map((p) => [p.productID, p]));
  const productUseStatus = new Map(
    productUsageList.filter((p) => p.totalPoundsUsed > 0).map((p) => [p.productID, 'unused' as 'unused' | 'used']),
  );
  const formRLineToChemicalsCalculated = new Map<
    FormRLeafLine,
    Map<CasOrCategory, Map<TransferID, CalculationLine<CasOrCategory>[]>>
  >();
  const casOrCategoryToThresholdCalculated = new Map<
    CasOrCategory,
    Map<UsageTypeID, CalculationLine<CasOrCategory>[]>
  >();
  const wasteStreamTotals = facilityWasteStreamTotalsCalculate(
    mode,
    profilesAndVariables,
    productUsage,
    processes,
    chemicalsPlusOriginals,
    {
      releaseProfileAnswers,
      releaseProfileFormulaChoices,
      wasteTransfers,
    },
  );
  const chemicalsWithoutReleasesSet = new Set<`${CasOrCategory}|${FormRLeafLine}`>(
    Array.from(chemicalsExceedingThresholds.values()).flatMap((casOrCategory) =>
      formRLeafLines.map((line) => `${casOrCategory}|${line}` as const),
    ),
  );

  const warnings: Warning[] = [...wasteStreamTotals.warnings];

  let lineCount = 0;
  for (const item of chemicalCalculationsExecute(
    mode,
    profilesAndVariables,
    productUsage,
    chemicalsPlusOriginals,
    chemicalsToTriCategories,
    processes,
    { releaseProfileAnswers, releaseProfileChemicalNaAnswers, releaseProfileFormulaChoices, wasteTransfers },
    usageMixResults,
    wasteStreamTotals.totals,
    chemicalSpecificQuestions,
  )) {
    if ('warningID' in item) {
      warnings.push(item);
      continue;
    } else if ('type' in item && item.type === 'notApplicable') {
      // If the chosen formula results in the profile not being applicable, we need to ensure the relevant chemicals
      // aren't flagged as missing their release calculations, since they will be properly marked as N/A. This also
      // ensures the release line will be able to be marked as complete.
      //
      // There is a chance the formula that was marked as N/A was only going to provide data for a subset of
      // chemicals, in which case this is overly broad. However, to determine this, we'd need to have a way of running
      // the not-chosen formula, which is not currently possible.
      for (const casOrCategory of chemicalsExceedingThresholds) {
        if (item.formRLeafLine) {
          chemicalsWithoutReleasesSet.delete(`${casOrCategory}|${item.formRLeafLine}`);
        }
      }
      continue;
    } else if ('type' in item && item.type === 'productSeen') {
      productUseStatus.set(item.productID, 'used');
      continue;
    }

    if (!item.casOrCategoryOriginal || !item.casOrCategoryMapped) {
      warnings.push({
        warningID: 'TW1132',
        type: 'internalConsistencyCheck',
        message: `Somehow a TRI calculation was performed without any chemical context.`,
      });
      continue;
    }

    const line = item as CalculationLine<CasOrCategory>; // Cast is guaranteed safe due to above check.
    const {
      basis,
      releaseProfileProcessName,
      releaseProfileID,
      releaseProfileName,
      thresholdOrRelease,
      thresholdUsageTypeID,
      transferID,
      casOrCategoryOriginal,
      casOrCategoryMapped,
      product,
    } = line;

    if (!UnitsTracker.fromParsedUnitString(line.computationUnits).isMass()) {
      if (releaseProfileID) {
        warnings.push({
          warningID: 'TW1076',
          type: 'releaseProfile',
          releaseProfileID,
          releaseProfileName,
          message: `The release profile "${releaseProfileName}" (${releaseProfileID}) has a formula which results in ${line.computationUnits} instead of mass (i.e. pounds or grams).`,
        });
      } else {
        warnings.push({
          warningID: 'TW1077',
          type: 'internalConsistencyCheck',
          message: `The release profile "${releaseProfileName}" (${releaseProfileID}) has a formula which results in ${line.computationUnits} instead of mass (i.e. pounds or grams).`,
        });
      }
    }

    if (line.lineID > lineCount) {
      lineCount = line.lineID;
    }

    let formRLeafLine = line.formRLeafLine;
    if (!formRLeafLine) {
      if (releaseProfileID) {
        warnings.push({
          warningID: 'TW1049',
          type: 'releaseProfile',
          releaseProfileID,
          releaseProfileName,
          message: `The release profile "${releaseProfileName}" (${releaseProfileID}) does not have a configured form R line, assuming 5.1.`,
        });
      } else {
        warnings.push({
          warningID: 'TW1075',
          type: 'internalConsistencyCheck',
          message: `The release profile "${releaseProfileName}" (${releaseProfileID}) does not have a configured form R line, assuming 5.1.`,
        });
      }
      formRLeafLine = '5.1';
    }
    if (basis === '' && releaseProfileID) {
      warnings.push({
        warningID: 'TW1056',
        type: 'releaseProfile',
        releaseProfileID,
        releaseProfileName,
        message: `Process "${releaseProfileProcessName}" using profile "${releaseProfileName}" - No basis for estimate was found.`,
      });
    }

    chemicalsWithoutReleasesSet.delete(`${casOrCategoryMapped}|${formRLeafLine}`);

    if (thresholdOrRelease === 'release' || thresholdOrRelease === 'both') {
      if (!formRLineToChemicalsCalculated.has(formRLeafLine)) {
        formRLineToChemicalsCalculated.set(formRLeafLine, new Map());
      }

      const chemsMap = formRLineToChemicalsCalculated.get(formRLeafLine)!;

      if (!chemsMap.has(casOrCategoryMapped)) {
        chemsMap.set(casOrCategoryMapped, new Map());
      }

      const transfersMap = chemsMap.get(casOrCategoryMapped)!;

      if (!transfersMap.has(transferID)) {
        transfersMap.set(transferID, []);
      }

      const releaseLines = transfersMap.get(transferID)!;

      releaseLines.push(line);
    }

    if (thresholdOrRelease === 'threshold' || thresholdOrRelease === 'both') {
      if (thresholdUsageTypeID === null) {
        warnings.push({
          warningID: 'TW1065',
          type: 'releaseProfileAssignment',
          formRLine: formRLeafLineToAssignmentLine(formRLeafLine),
          message: `While calculating process "${releaseProfileProcessName}" with profile "${releaseProfileName}"${product ? ` for product with ID "${product.productID}"` : ''} for chemical "${casOrCategoryOriginal}" we were not able to find a threshold usage type when computing thresholds.`,
        });
      } else {
        if (!casOrCategoryToThresholdCalculated.has(casOrCategoryMapped)) {
          casOrCategoryToThresholdCalculated.set(casOrCategoryMapped, new Map());
        }

        const usageTypeMap = casOrCategoryToThresholdCalculated.get(casOrCategoryMapped)!;

        if (!usageTypeMap.has(thresholdUsageTypeID)) {
          usageTypeMap.set(thresholdUsageTypeID, []);
        }

        const thresholdLines = usageTypeMap.get(thresholdUsageTypeID)!;

        thresholdLines.push(line);
      }
    }
  }

  const chemicalsWithoutReleasesArr: { casOrCategory: CasOrCategory; formRLine: FormRLeafLine }[] = [];
  for (const casAndLine of chemicalsWithoutReleasesSet) {
    const [casOrCategory, formRLeafLine] = casAndLine.split('|') as [CasOrCategory, FormRLeafLine];
    const attestation = attestations[formRLeafLine][casOrCategory]?.attestation;
    if (attestation === 'no-releases') {
      // Nothing to do, our Form R export will mark N/A automatically if we emit no release lines.
    } else if (attestation === 'zero-releases') {
      const basisFromAttestation = attestations[formRLeafLine][casOrCategory]?.basis ?? '';

      if (basisFromAttestation === '') {
        warnings.push({
          warningID: 'TW1066',
          type: 'releaseProfileAssignment',
          message: `${casOrCategory} was attested as a zero (0) pounds release for ${formRLeafLine}, but no basis for estimate was found.`,
          formRLine: formRLeafLineToAssignmentLine(formRLeafLine),
        });
      }

      if (!formRLineToChemicalsCalculated.has(formRLeafLine)) {
        formRLineToChemicalsCalculated.set(formRLeafLine, new Map());
      }

      const chemsMap = formRLineToChemicalsCalculated.get(formRLeafLine)!;

      if (!chemsMap.has(casOrCategory)) {
        chemsMap.set(casOrCategory, new Map());
      }

      const wasteStreamID = zeroReleasesWasteStreamID(formRLeafLine, casOrCategory);
      const wasteTransfersForZero: Pick<WasteTransfer, 'transferID' | 'allocationOrAnswer'>[] = Object.values(
        wasteTransfers,
      ).filter((t): t is WasteTransfer => t?.wasteStreamID === wasteStreamID);
      if (wasteTransfersForZero.length === 0) {
        wasteTransfersForZero.push({
          transferID: 'transfer_default',
          allocationOrAnswer: 0,
        });
      }
      const transfersMap = chemsMap.get(casOrCategory)!;

      for (const { transferID, allocationOrAnswer } of wasteTransfersForZero) {
        if (!transfersMap.has(transferID)) {
          transfersMap.set(transferID, []);
        }

        if (typeof allocationOrAnswer === 'number' && allocationOrAnswer !== 0) {
          warnings.push({
            warningID: 'TW1067',
            type: 'questionnaire',
            subType: zeroReleasesQuestionID(formRLeafLine, casOrCategory),
            casOrCategory,
            message: `${casOrCategory} was attested as a zero (0) pounds release for ${formRLeafLine}, but ${allocationOrAnswer} pounds were specified in a transfer.`,
            blocksPriorYearSection8Calculation: true,
          });
        }

        const lines = transfersMap.get(transferID)!;
        const units = casOrCategory === 'N150' ? 'grams' : 'pounds';
        lines.push({
          lineID: ++lineCount,
          transferID,
          wasteStreamID,
          releaseProfileProcessName: 'Attested Zero (0) Releases',
          releaseProfileID: null,
          releaseProfileFormulaID: null,
          releaseProfileOutputID: null,
          releaseProfileName: 'Attested Zero (0) Releases',
          releaseProfileFormulaName: 'Attested Zero (0) Releases',
          formRLeafLine,
          basis: basisFromAttestation,
          thresholdOrRelease: 'release',
          thresholdUsageTypeID: null,
          formulaOutputType: 'actual',
          potentialBasisPeriod: { unit: 'year', count: 1 },
          product: null,
          casOrCategoryOriginal: casOrCategory,
          casOrCategoryMapped: casOrCategory,
          computationResult: 0,
          computationUnits: units,
          convertedResult: 0,
          convertedUnits: units,
          formulaWithVariables: '0',
          formulasWithValues: [
            {
              formula: [{ name: 'Attested_Zero', value: '0' }],
              value: 0,
              units,
            },
          ],
          subFormulas: [],
          variablesUsed: { Attested_Zero: { value: 0, units } },
        });
      }
    } else {
      warnings.push({
        warningID: 'TW1058',
        type: 'releaseProfileAssignment',
        formRLine: formRLeafLineToAssignmentLine(formRLeafLine),
        message: `${casOrCategory} exceeded TRI reporting threshold, but no releases were recorded against it for form R line ${formRLeafLine}.`,
      });
      chemicalsWithoutReleasesArr.push({ casOrCategory, formRLine: formRLeafLine });
    }
  }

  const warningsDeduped = new Map(warnings.map((w) => [w.message, w]));
  const pounds = UnitsTracker.fromSingleUnit(Pound);
  const grams = UnitsTracker.fromSingleUnit(Gram);

  return {
    warnings: Array.from(warningsDeduped.values()),
    chemicalsWithoutReleases: chemicalsWithoutReleasesArr,
    thresholdsFromFormulas: Array.from(casOrCategoryToThresholdCalculated.entries()).flatMap(
      ([casOrCategory, byUsageTypeID]): ThresholdComputed[] => {
        return Array.from(byUsageTypeID.entries()).map(([usageTypeID, calculationLines]): ThresholdComputed => {
          let totalPoundsUsed = 0;
          let totalGramsUsed = 0;

          for (const line of calculationLines) {
            const lineUnits = UnitsTracker.fromParsedUnitString(line.computationUnits);
            const convertedToPounds = lineUnits.convertTo(pounds, line.computationResult);
            if (typeof convertedToPounds === 'number') {
              totalPoundsUsed += convertedToPounds;
            } else {
              // The TW1076 warning above will surface this warning for us in the UI:
              console.warn('triReleasesCalculate unit conversion error:', line, convertedToPounds.message);
            }

            const convertedToGrams = lineUnits.convertTo(grams, line.computationResult);
            if (typeof convertedToGrams === 'number') {
              totalGramsUsed += convertedToGrams;
            } else {
              // The TW1076 warning above will surface this warning for us in the UI:
              console.warn('triReleasesCalculate unit conversion error:', line, convertedToGrams.message);
            }
          }

          return {
            casOrCategory,
            usageTypeID,
            totalPoundsUsed,
            totalGramsUsed,
            calculationLines,
          };
        });
      },
    ),
    releases: Array.from(formRLineToChemicalsCalculated.entries())
      .map(([formRLine, chems]) => {
        return {
          formRLine: formRLine,
          chemicalReleases: Array.from(chems.entries())
            .map(([casOrCategory, linesByTransferID]): ReleaseComputed['chemicalReleases'][number] => {
              let computationResultTotal = 0;
              const transfers: TransferComputed[] = [];
              const amountUnits: 'grams' | 'pounds' = casOrCategory === 'N150' ? 'grams' : 'pounds';
              const amountUnitsObj = casOrCategory === 'N150' ? grams : pounds;

              for (const [transferID, calculationLines] of linesByTransferID.entries()) {
                let amountReleased = 0;
                for (const line of calculationLines) {
                  const converted = UnitsTracker.fromParsedUnitString(line.computationUnits).convertTo(
                    amountUnitsObj,
                    line.computationResult,
                  );
                  if (typeof converted === 'number') {
                    amountReleased += converted;
                    line.convertedResult = converted;
                    line.convertedUnits = amountUnits;
                  } else {
                    // The TW1076 warning above will surface this warning for us in the UI:
                    console.warn('triReleasesCalculate unit conversion error:', line, converted.message);
                  }
                }

                let isFromStormwater = false;
                if (formRLine === '5.3') {
                  // Lines within a transfer should always share the same wasteStreamID:
                  const wasteStreamID = calculationLines[0]?.wasteStreamID ?? 'waste_stream_none';
                  const wasteStreamTotal = wasteStreamTotals.totals[wasteStreamID];
                  if (wasteStreamTotal) {
                    isFromStormwater = wasteStreamTotal.isFromStormwater;
                  }
                }

                computationResultTotal += amountReleased;
                transfers.push({
                  transferID,
                  amountReleased,
                  amountUnits,
                  isFromStormwater,
                  calculationLines,
                });
              }

              return {
                casOrCategory,
                amountReleased: computationResultTotal,
                amountUnits,
                transfers,
              };
            })
            .sort((a, b) => {
              return a.casOrCategory.localeCompare(b.casOrCategory);
            }),
        };
      })
      .sort((a, b) => {
        return a.formRLine.localeCompare(b.formRLine);
      }),
    wasteStreamTotals: wasteStreamTotals.totals,
    productUseStatus,
  };
}
